/**
 * @format
 */
import { ValidatablePlan } from 'common-typescript';
import { Education, Plan, StudyRight } from 'common-typescript/types';
import * as _ from 'lodash-es';
import { pickMostRelevantStudyRight } from './study-right-entity.service';

const NOT_ENDED_STUDY_RIGHT_STATES: StudyRight['state'][] = ['ACTIVE', 'ACTIVE_NONATTENDING', 'PASSIVE', 'NOT_STARTED'];

export function findMatchingStudyRight(plan: Plan, studyRights: StudyRight[]): StudyRight {
    return pickMostRelevantStudyRight(
        studyRights,
        studyRight => isForPlan(studyRight, plan) && NOT_ENDED_STUDY_RIGHT_STATES.includes(studyRight.state),
    );
}

// Same match as findMatchingStudyRight but without the study right state check, so an ended (e.g. EXPIRED) study right still
// matches. Module content workflow applications must still be able to reference the study right they were created under,
// even after it has ended - see findMatchingStudyRight for the state-restricted variant used everywhere else.
export function findMatchingStudyRightIgnoringState(plan: Plan, studyRights: StudyRight[]): StudyRight {
    return pickMostRelevantStudyRight(studyRights, studyRight => isForPlan(studyRight, plan));
}

function isForPlan(studyRight: StudyRight, plan: Plan): boolean {
    return (
        studyRight.educationId === plan.rootId &&
        studyRight.learningOpportunityId === plan.learningOpportunityId &&
        studyRight.documentState === 'ACTIVE'
    );
}

/**
 * Term registration is intentionally not checked here: professional further education (e.g. erikoislääkärikoulutus)
 * study rights are degree educations but don't require term registration, so requiring it here incorrectly blocked
 * custom attainment and inclusion applications for them. The backend validates the full eligibility rule.
 */
export function canApplyForCustomAttainments(validatablePlan: ValidatablePlan, education: Education, studyRight: StudyRight): boolean {
    if (!validatablePlan || !education || !studyRight || !isDegreeEducation(education) || studyRight.state !== 'ACTIVE') {
        return false;
    }
    const educationChildModules = validatablePlan.getSelectedModulesUnderModule(education);
    return !_.isEmpty(educationChildModules) && _.every(educationChildModules, { type: 'DegreeProgramme' });
}

function isDegreeEducation(education: Education): boolean {
    return _.startsWith(education.educationType, 'urn:code:education-type:degree-education');
}
